import {
    type AccountPaymentsSuccessResponse,
    type AccountRemovePaymentResponse,
    type AddPaymentErrorResponse,
    CardTokenizationError,
} from '@libs/common-next';
import type { AdyenPaymentMethodsResponse } from '@libs/common-node';
import { getLocale, LocaleShorthand, NotFoundError } from '@libs/core';

import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';
import { type ApiResponse, unwrapApiResponseOrThrow } from './utils';

const CARD_TOKENIZATION_REJECTED_MESSAGE = 'Credit Card tokenization is rejected by psp';
const CARD_REJECTED_MESSAGE = 'Could not add card';
const PAYMENT_PROCESSOR_ERROR_MESSAGE = '[BFF] Error reaching payment processor';

const PAYMENT_KNOWN_ERROR_DETAILS = [
    CARD_TOKENIZATION_REJECTED_MESSAGE,
    CARD_REJECTED_MESSAGE,
    PAYMENT_PROCESSOR_ERROR_MESSAGE,
];

const throwIfKnownPaymentError = (response: unknown) => {
    if (response && typeof response === 'object' && 'detail' in response) {
        const detail = (response as { detail: string }).detail;

        const hasKnownError = PAYMENT_KNOWN_ERROR_DETAILS.some(error => detail.includes(error));

        if (hasKnownError) throw new CardTokenizationError((response as { detail: string }).detail);
    }
};

export async function addPayment(body: Record<string, unknown>) {
    const response = await cweaClient.post<
        ApiResponse<AccountPaymentsSuccessResponse | AddPaymentErrorResponse>,
        Record<string, unknown>
    >(CWEA_ENDPOINTS.payments.addPayment, {
        body,
        additionalSuccessCodes: [400],
    });

    throwIfKnownPaymentError(response);

    return unwrapApiResponseOrThrow(response, 'Failed to add payment method');
}

export async function updatePayment(cardId: number, body: Record<string, unknown>) {
    const response = await cweaClient.put<
        ApiResponse<AccountPaymentsSuccessResponse | AddPaymentErrorResponse>,
        Record<string, unknown>
    >(CWEA_ENDPOINTS.payments.updatePayment(cardId), {
        body,
        additionalSuccessCodes: [400],
    });

    throwIfKnownPaymentError(response);

    return unwrapApiResponseOrThrow(response, 'Failed to update payment method');
}

export async function getAdyenPaymentMethods(countryCode: 'us' | 'ca'): Promise<AdyenPaymentMethodsResponse> {
    const result = await cweaClient.get<AdyenPaymentMethodsResponse>(
        `${CWEA_ENDPOINTS.payments.adyenMethods}?countryCode=${countryCode}`,
        { useOldFetch: true }
    );

    if (!result) {
        throw new Error(`${CWEA_ENDPOINTS.payments.adyenMethods} returned null`);
    }

    return result;
}

export async function removePayment(cardId: number) {
    const response = await cweaClient.delete<AccountRemovePaymentResponse>(
        CWEA_ENDPOINTS.payments.removePayment(cardId),
        {
            additionalSuccessCodes: [404],
        }
    );

    if (response == null) return null;

    if ('status' in response && response.status === 404) {
        throw new NotFoundError();
    }

    return unwrapApiResponseOrThrow(response, 'Failed to remove payment method');
}

export type FraudCvvAttemptCountResponse = {
    fraudCVVAttemptCount: number | null;
};

export type CvvValidationResponse = {
    validated: boolean;
    fraudCVVAttemptCount: number;
    resultCode?: string;
    refusalReason?: string;
    pspReference?: string;
};

export async function getFraudCvvAttemptCount(): Promise<FraudCvvAttemptCountResponse> {
    const response = await cweaClient.get<FraudCvvAttemptCountResponse>(CWEA_ENDPOINTS.account.fraudCvvAttemptCount);

    return unwrapApiResponseOrThrow(response, 'Failed to fetch CVV attempt count');
}

export async function validatePaymentCvv(
    cardId: number,
    encryptedSecurityCode: string
): Promise<CvvValidationResponse> {
    const response = await cweaClient.post<
        CvvValidationResponse,
        { encryptedSecurityCode: string; countryCode: string }
    >(CWEA_ENDPOINTS.payments.validatePayment(cardId), {
        body: { encryptedSecurityCode, countryCode: LocaleShorthand[getLocale()] },
    });

    return unwrapApiResponseOrThrow(response, 'Failed to validate payment CVV');
}
